import {
  appendRow,
  deleteRow,
  ensureTab,
  findRowIndexById,
  getRows,
  updateRow
} from '../lib/googleSheets';

export const entities={
  profile: {
    title: 'SiteProfile',
    headers: [
      'id',
      'display_name',
      'tagline',
      'bio',
      'image_url',
      'created_at',
      'updated_at'
    ]
  },
  socials: {
    title: 'SocialLinks',
    headers: [
      'id',
      'platform',
      'label',
      'url',
      'order',
      'active',
      'created_at',
      'updated_at'
    ]
  },
  links: {
    title: 'BioLinks',
    headers: [
      'id',
      'title',
      'description',
      'url',
      'image_url',
      'category',
      'featured',
      'archived',
      'order',
      'click_count',
      'created_at',
      'updated_at'
    ]
  },
  deals: {
    title: 'LTDDeals',
    headers: [
      'id',
      'title',
      'description',
      'url',
      'image_url',
      'category',
      'price',
      'featured',
      'order',
      'click_count',
      'created_at',
      'updated_at'
    ]
  },
  portfolio: {
    title: 'PortfolioItems',
    headers: [
      'id',
      'title',
      'description',
      'url',
      'image_url',
      'category',
      'type',
      'featured',
      'order',
      'click_count',
      'created_at',
      'updated_at',
      'subcategory'
    ]
  },
  music: {
    title: 'MusicTracks',
    headers: [
      'id',
      'title',
      'artist',
      'url',
      'image_url',
      'platform',
      'order',
      'click_count',
      'created_at',
      'updated_at'
    ]
  },
  amazonFinds: {
    title: 'AmazonFinds',
    headers: [
      'id',
      'title',
      'description',
      'url',
      'image_url',
      'category',
      'price_label',
      'featured',
      'active',
      'order',
      'click_count',
      'created_at',
      'updated_at'
    ]
  }
};

let profileImageSync;
const pendingRecordEnsures=new Map();

const normalizeValue=(value)=> {
  if (value===true) return 'TRUE';
  if (value===false) return 'FALSE';
  return value ?? '';
};

const normalizeUrl=(url)=> String(url || '')
  .trim()
  .replace(/\/$/,'')
  .toLowerCase();

const normalizeTitle=(title)=> String(title || '').trim().toLowerCase();

const rowToObject=(headers,row=[])=> Object.fromEntries(
  headers.map((header,index)=> [header,row[index]])
);

const objectToRow=(headers,record)=> headers.map(
  (header)=> normalizeValue(record[header])
);

function config(entity) {
  const selected=entities[entity];

  if (!selected) {
    throw new Error(`Unknown content entity: ${entity}`);
  }

  return selected;
}

function recordNeedsUpdate(existing,values) {
  return Object.entries(values).some(([key,value])=> {
    if (key==='id' || value===undefined) return false;
    return String(existing[key] ?? '')!==String(normalizeValue(value));
  });
}

function findSyncedRecord(entity,records,values) {
  const urlMatch=records.find(
    (record)=> normalizeUrl(record.url)===normalizeUrl(values.url)
  );

  if (urlMatch) return urlMatch;

  if ((entity==='portfolio' || entity==='amazonFinds') && values.title) {
    return records.find(
      (record)=> normalizeTitle(record.title)===normalizeTitle(values.title)
    );
  }

  return undefined;
}

export async function listRecords(entity) {
  const {title,headers}=config(entity);
  await ensureTab(title,headers);
  const rows=await getRows(`${title}!A2:Z`);

  return rows
    .filter((row)=> row?.[0])
    .map((row)=> rowToObject(headers,row));
}

export async function getRecord(entity,id) {
  const {title,headers}=config(entity);
  await ensureTab(title,headers);
  const sheetRow=await findRowIndexById(title,id);

  if (sheetRow < 0) return null;

  const rows=await getRows(`${title}!A${sheetRow}:Z${sheetRow}`);
  return rows[0] ? rowToObject(headers,rows[0]) : null;
}

export async function createRecord(entity,values) {
  const {title,headers}=config(entity);
  const now=new Date().toISOString();
  const record={
    ...values,
    id: crypto.randomUUID(),
    created_at: now,
    updated_at: now
  };

  await ensureTab(title,headers);
  await appendRow(`${title}!A:Z`,objectToRow(headers,record));
  return record;
}

export async function ensureRecordByUrl(entity,values) {
  const normalizedUrl=normalizeUrl(values.url);
  const cacheKey=`${entity}:${normalizedUrl}`;

  if (!normalizedUrl) {
    throw new Error(`A URL is required to sync ${entity} content`);
  }

  if (pendingRecordEnsures.has(cacheKey)) {
    return pendingRecordEnsures.get(cacheKey);
  }

  const request=(async ()=> {
    const records=await listRecords(entity);
    const existing=findSyncedRecord(entity,records,values);

    if (!existing) {
      return createRecord(entity,values);
    }

    if (recordNeedsUpdate(existing,values)) {
      return updateRecord(entity,existing.id,values);
    }

    return existing;
  })();

  pendingRecordEnsures.set(cacheKey,request);

  try {
    return await request;
  } finally {
    pendingRecordEnsures.delete(cacheKey);
  }
}

export async function syncRecordsByUrl(entity,records) {
  const synced=[];

  for (const sourceRecord of records) {
    const values={...sourceRecord};
    delete values.id;
    synced.push(await ensureRecordByUrl(entity,values));
  }

  return synced;
}

export async function updateRecord(entity,id,values) {
  const {title,headers}=config(entity);
  await ensureTab(title,headers);
  const existing=await getRecord(entity,id);

  if (!existing) {
    throw new Error(`Record not found: ${id}`);
  }

  const sheetRow=await findRowIndexById(title,id);

  if (sheetRow < 0) {
    throw new Error(`Record not found: ${id}`);
  }

  const record={
    ...existing,
    ...values,
    id,
    created_at: existing.created_at,
    updated_at: new Date().toISOString()
  };

  await updateRow(
    `${title}!A${sheetRow}:Z${sheetRow}`,
    objectToRow(headers,record)
  );

  return record;
}

export async function removeRecord(entity,id) {
  const {title,headers}=config(entity);
  await ensureTab(title,headers);
  return deleteRow(title,id);
}

export async function incrementClicks(entity,record) {
  if (!record?.id) return;
  const current=Number(record.click_count || 0);
  await updateRecord(entity,record.id,{click_count: current + 1});
}

export function persistProfileImage(imageUrl,profileDefaults) {
  if (profileImageSync) return profileImageSync;

  profileImageSync=(async ()=> {
    const profiles=await listRecords('profile');
    const currentProfile=profiles[0];

    if (!currentProfile) {
      return createRecord('profile',{
        ...profileDefaults,
        image_url: imageUrl
      });
    }

    if (currentProfile.image_url===imageUrl) {
      return currentProfile;
    }

    return updateRecord('profile',currentProfile.id,{
      image_url: imageUrl
    });
  })().catch((error)=> {
    profileImageSync=undefined;
    throw error;
  });

  return profileImageSync;
}